import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { WSPacket, WebSocketStatus, Client, Repository, AlertConfig, AlertLog, BackupLog, User, AlertTemplate } from '@/types'
import { useAuthStore } from './auth'

export const useWebSocketStore = defineStore('websocket', () => {
  const socket = ref<WebSocket | null>(null)
  const status = ref<WebSocketStatus>('disconnected')
  const lastError = ref<string | null>(null)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = 3000
  const isConnecting = ref(false)
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  const clients = ref<Client[]>([])
  const repositories = ref<Repository[]>([])
  const alertConfigs = ref<AlertConfig[]>([])
  const alertTemplates = ref<AlertTemplate[]>([])
  const alertLogs = ref<AlertLog[]>([])
  const backupLogs = ref<BackupLog[]>([])
  const users = ref<User[]>([])
  const currentClientLogs = ref<BackupLog[]>([])

  const isConnected = computed(() => status.value === 'connected')
  const clientCount = computed(() => clients.value.length)
  const activeAlertCount = computed(() => {
    return clients.value.reduce((sum, client) => sum + (client.active_alerts_count || 0), 0)
  })

  function connect(): void {
    if (isConnecting.value) {
      console.log('WebSocket connection already in progress')
      return
    }

    if (socket.value?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected')
      return
    }

    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    isConnecting.value = true
    status.value = 'connecting'
    lastError.value = null

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${protocol}//${window.location.host}/ws`
      
      console.log('Opening WebSocket connection...')
      socket.value = new WebSocket(wsUrl)

      socket.value.onopen = () => {
        console.log('WebSocket connected')
        isConnecting.value = false
        status.value = 'connected'
        reconnectAttempts.value = 0
        
        console.log('Fetching initial data...')
        fetchData('clients')
        fetchData('repositories')
        fetchData('alerts')
        fetchData('alert_templates')
        fetchData('users')
      }

      socket.value.onmessage = (event) => {
        try {
          const message: WSPacket = JSON.parse(event.data)
          handleMessage(message)
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      socket.value.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason)
        isConnecting.value = false
        status.value = 'disconnected'
        const wasConnected = socket.value !== null
        socket.value = null
        
        if (wasConnected && reconnectAttempts.value < maxReconnectAttempts) {
          reconnectAttempts.value++
          console.log(`Attempting to reconnect (${reconnectAttempts.value}/${maxReconnectAttempts})...`)
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null
            if (useAuthStore().isAuthenticated) {
              connect()
            }
          }, reconnectDelay * reconnectAttempts.value)
        }
      }

      socket.value.onerror = (error) => {
        console.error('WebSocket error:', error)
        isConnecting.value = false
        status.value = 'error'
        lastError.value = 'Connection error'
      }
    } catch (err) {
      console.error('Failed to create WebSocket:', err)
      isConnecting.value = false
      status.value = 'error'
      lastError.value = 'Failed to establish connection'
    }
  }

  function disconnect(): void {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    if (socket.value) {
      socket.value.close()
      socket.value = null
    }
    isConnecting.value = false
    status.value = 'disconnected'
    reconnectAttempts.value = 0
    
    clients.value = []
    repositories.value = []
    alertConfigs.value = []
    alertTemplates.value = []
    alertLogs.value = []
    backupLogs.value = []
    users.value = []
    currentClientLogs.value = []
  }

  function fetchData(topic: string): void {
    if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
      console.warn('Cannot fetch data, WebSocket not connected')
      return
    }

    socket.value.send(JSON.stringify({
      type: 'SUBSCRIBE',
      topic
    }))
  }

  function fetchClientLogs(clientId: string): void {
    if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
      console.warn('Cannot fetch client logs, WebSocket not connected')
      return
    }

    socket.value.send(JSON.stringify({
      type: 'SUBSCRIBE',
      topic: `backup_logs:${clientId}`
    }))
  }

  function fetchBackupLog(backupLogId: number): void {
    if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
      console.warn('Cannot fetch backup log, WebSocket not connected')
      return
    }

    socket.value.send(JSON.stringify({
      type: 'SUBSCRIBE',
      topic: `backup_log:${backupLogId}`
    }))
  }

  function clearClientLogs(): void {
    currentClientLogs.value = []
  }

  function handleMessage(message: WSPacket): void {
    switch (message.type) {
      case 'AUTH':
        if (message.payload === 'success') {
          console.log('WebSocket authenticated')
        } else {
          console.error('WebSocket authentication failed')
          disconnect()
        }
        break

      case 'DATA':
        handleDataMessage(message)
        break

      case 'UPDATE':
        handleUpdateMessage(message)
        break

      case 'ERROR':
        console.error('WebSocket error message:', message.payload)
        lastError.value = typeof message.payload === 'string' ? message.payload : 'Unknown error'
        break

      default:
        console.log('Unknown message type:', message.type)
    }
  }

  function handleDataMessage(message: WSPacket): void {
    const topic = message.topic || ''
    const payload = message.payload as any

    if (topic === 'clients') {
      clients.value = payload || []
    } else if (topic === 'repositories') {
      repositories.value = payload || []
    } else if (topic === 'alerts') {
      alertConfigs.value = payload?.configs || []
      alertLogs.value = payload?.logs || []
    } else if (topic === 'alert_templates') {
      alertTemplates.value = payload || []
    } else if (topic === 'users') {
      users.value = payload || []
    } else if (topic.startsWith('backup_logs:')) {
      currentClientLogs.value = payload || []
    } else if (topic === 'backup_log') {
      // Single backup log fetch - emit via event or callback
      // The caller should listen for this data
      window.dispatchEvent(new CustomEvent('backup-log-data', { detail: payload }))
    }
  }

  function handleUpdateMessage(message: WSPacket): void {
    const topic = message.topic || ''
    const payload = message.payload as any

    if (topic === 'client_connected' || topic === 'client_disconnected') {
      const clientIndex = clients.value.findIndex(c => c.uuid === payload.client_id)
      if (clientIndex !== -1) {
        // Update existing client
        const updatedClients = [...clients.value]
        const update: Partial<Client> = {
          is_connected: topic === 'client_connected'
        }
        if (topic === 'client_disconnected' && payload.last_connected) {
          update.last_connected = { Valid: true, Int64: payload.last_connected }
        }
        updatedClients[clientIndex] = {
          ...updatedClients[clientIndex],
          ...update
        }
        clients.value = updatedClients
        console.log(`Client ${payload.client_id} ${topic === 'client_connected' ? 'connected' : 'disconnected'}`)
      } else if (topic === 'client_connected') {
        // New client not in list - add it from the payload data
        if (payload.uuid) {
          const newClient: Client = {
            uuid: payload.uuid,
            simple_name: payload.simple_name || payload.client_id,
            enrolled_at: payload.enrolled_at || 0,
            is_connected: true,
            last_connected: payload.last_connected ? { Valid: true, Int64: payload.last_connected } : null,
            version: payload.version
          }
          clients.value = [...clients.value, newClient]
          console.log(`New client ${payload.client_id} added to list`)
        }
      }
    } else if (topic === 'client_info_updated') {
      // Handle version update from GetSystemInfo response
      const clientIndex = clients.value.findIndex(c => c.uuid === payload.client_id)
      if (clientIndex !== -1 && payload.version) {
        const updatedClients = [...clients.value]
        updatedClients[clientIndex] = {
          ...updatedClients[clientIndex],
          version: { String: payload.version, Valid: true }
        }
        clients.value = updatedClients
        console.log(`Client ${payload.client_id} version updated to ${payload.version}`)
      }
    } else if (topic === 'backup_completed') {
      const clientIndex = clients.value.findIndex(c => c.uuid === payload.client_id)
      if (clientIndex !== -1) {
        const updatedClients = [...clients.value]
        updatedClients[clientIndex] = {
          ...updatedClients[clientIndex],
          latest_backup: payload.backup,
          backup_progress: undefined // Clear progress on completion
        }
        clients.value = updatedClients
        console.log(`Backup completed for client ${payload.client_id}`)
      }
    } else if (topic === 'backup_progress') {
      const clientIndex = clients.value.findIndex(c => c.uuid === payload.client_id)
      if (clientIndex !== -1) {
        const updatedClients = [...clients.value]
        const client = updatedClients[clientIndex]
        if (payload.progress.complete) {
          // Clear progress if marked complete
          updatedClients[clientIndex] = {
            ...client,
            backup_progress: undefined
          }
        } else {
          updatedClients[clientIndex] = {
            ...client,
            backup_progress: payload.progress
          }
        }
        clients.value = updatedClients
      }
    } else if (topic === 'alert_triggered') {
      const clientIndex = clients.value.findIndex(c => c.uuid === payload.client_id)
      if (clientIndex !== -1) {
        const updatedClients = [...clients.value]
        updatedClients[clientIndex] = {
          ...updatedClients[clientIndex],
          active_alerts_count: (updatedClients[clientIndex].active_alerts_count || 0) + 1
        }
        clients.value = updatedClients
      }
    } else if (topic === 'repositories') {
      fetchData('repositories')
    } else if (topic === 'clients') {
      fetchData('clients')
    } else if (topic === 'alerts') {
      fetchData('alerts')
    } else if (topic === 'alert_templates') {
      fetchData('alert_templates')
    }
  }

  return {
    socket,
    status,
    lastError,
    isConnected,
    clients,
    repositories,
    alertConfigs,
    alertTemplates,
    alertLogs,
    backupLogs,
    users,
    currentClientLogs,
    clientCount,
    activeAlertCount,
    connect,
    disconnect,
    fetchData,
    fetchClientLogs,
    fetchBackupLog,
    clearClientLogs
  }
})
